import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Brain, ArrowLeft, Share2, Sparkles } from 'lucide-react';

export default function InnerBattleBrain() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => { if (user) api.get('/games/state').then(r => setStats(r.data)).catch(() => {}); }, [user]);

  // Compute alignment 0..1 (1 = fully growth side). Based on recent positive actions (logs), streak, XP.
  const log = stats?.actions_log || [];
  const positiveActions = log.filter(a => (a.score || 0) > 0).length;
  const total = Math.max(1, log.length);
  const baseRatio = positiveActions / total;
  const streakBoost = Math.min(0.3, (stats?.streak || 0) * 0.02);
  const alignment = Math.min(1, Math.max(0.1, baseRatio + streakBoost));

  // Visual values
  const goldOpacity = alignment;        // right side intensity
  const darkOpacity = 1 - alignment;     // left side intensity
  const cracks = Math.round(8 * darkOpacity); // number of crack lines
  const sparkles = Math.round(12 * goldOpacity);
  const stage = alignment > 0.75 ? 'Unshakable' : alignment > 0.6 ? 'Focused' : alignment > 0.4 ? 'Rebuilding' : alignment > 0.25 ? 'Stabilizing' : 'Surviving';

  return (
    <div className="bg-ink text-white min-h-screen overflow-hidden">
      <div className="kc-container py-6">
        <Link to="/games" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-white/60 hover:text-[color:var(--kc-gold)]" data-testid="back-games"><ArrowLeft size={12}/> Games</Link>
        <div className="mt-4 text-center">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold"><Brain size={12}/> Inner Battle</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Your brain today.</h1>
          <p className="mt-2 text-white/70 max-w-lg mx-auto">A living visualization of the work. The more you train, the more the light side wins.</p>
        </div>

        <div className="mt-6 mx-auto max-w-3xl" data-testid="brain-arena">
          <div className="relative aspect-[3/2] rounded-[var(--kc-radius-md)] overflow-hidden" style={{ background: 'radial-gradient(800px 600px at 50% 50%, #1a1a1c, #0B0B0C 70%)' }}>
            <svg viewBox="0 0 600 400" className="w-full h-full">
              <defs>
                <radialGradient id="darkSide" cx="30%" cy="50%">
                  <stop offset="0%" stopColor="#3a3a40" stopOpacity={darkOpacity}/>
                  <stop offset="100%" stopColor="#0B0B0C" stopOpacity="0"/>
                </radialGradient>
                <radialGradient id="goldSide" cx="70%" cy="50%">
                  <stop offset="0%" stopColor="#F0C660" stopOpacity={goldOpacity}/>
                  <stop offset="100%" stopColor="#D4A437" stopOpacity="0"/>
                </radialGradient>
                <filter id="glow"><feGaussianBlur stdDeviation="6" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
              </defs>
              {/* atmospheric halos */}
              <rect width="600" height="400" fill="url(#darkSide)"/>
              <rect width="600" height="400" fill="url(#goldSide)"/>
              {/* center divider */}
              <line x1="300" y1="30" x2="300" y2="370" stroke="rgba(212,164,55,0.25)" strokeDasharray="4 6"/>

              {/* Brain outline (simplified two-hemisphere) */}
              <g transform="translate(300 200)" filter="url(#glow)">
                {/* Left hemisphere - darker */}
                <path d="M -10 -100 Q -130 -100 -130 0 Q -130 90 -10 110 Z" fill={`rgba(70,55,40,${0.4 + darkOpacity*0.4})`} stroke={`rgba(150,120,80,${darkOpacity*0.7})`} strokeWidth="2"/>
                {/* Right hemisphere - lighter/gold */}
                <path d="M 10 -100 Q 130 -100 130 0 Q 130 90 10 110 Z" fill={`rgba(212,164,55,${0.2 + goldOpacity*0.5})`} stroke={`rgba(240,198,96,${goldOpacity*0.95})`} strokeWidth="2"/>
                {/* gyri (folds) */}
                {[0,1,2,3].map(i => (<path key={`L${i}`} d={`M -120 ${-60 + i*32} Q -60 ${-50 + i*32} -10 ${-55 + i*32}`} fill="none" stroke={`rgba(150,120,80,${0.3 + darkOpacity*0.5})`} strokeWidth="1.5"/>))}
                {[0,1,2,3].map(i => (<path key={`R${i}`} d={`M 10 ${-55 + i*32} Q 60 ${-45 + i*32} 120 ${-50 + i*32}`} fill="none" stroke={`rgba(240,198,96,${0.4 + goldOpacity*0.6})`} strokeWidth="1.5"/>))}
                {/* cracks on dark side */}
                {Array.from({length: cracks}).map((_, i) => { const y = -80 + i * 25; return (<line key={`c${i}`} x1="-100" y1={y} x2={-30 - Math.random()*40} y2={y + 10} stroke="rgba(200,80,80,0.6)" strokeWidth="0.8"/>); })}
                {/* sparkles on gold side */}
                {Array.from({length: sparkles}).map((_, i) => { const a = Math.random() * Math.PI; const r = 30 + Math.random() * 80; return (<circle key={`s${i}`} cx={Math.cos(a) * r} cy={(Math.random() - 0.5) * 120} r={1.5 + Math.random()*1.5} fill="#F0C660"><animate attributeName="opacity" values="0.2;1;0.2" dur={`${2 + Math.random()*2}s`} repeatCount="indefinite" begin={`${Math.random()*2}s`}/></circle>); })}
              </g>
              {/* Labels */}
              <text x="40" y="40" fill="rgba(200,80,80,0.7)" fontFamily="Bebas Neue, sans-serif" fontSize="18" letterSpacing="2">FEAR · DOUBT · NOISE</text>
              <text x="360" y="40" fill="rgba(240,198,96,0.95)" fontFamily="Bebas Neue, sans-serif" fontSize="18" letterSpacing="2">PURPOSE · PEACE · GROWTH</text>
            </svg>
          </div>

          <div className="mt-5 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--kc-gold)]/10 border border-[color:var(--kc-gold)]/30 text-[color:var(--kc-gold)] font-display uppercase tracking-wider" data-testid="brain-stage"><Sparkles size={14}/> {stage}</div>
            <div className="mt-3 text-sm text-white/70">Light side is winning <span className="text-[color:var(--kc-gold)] font-bold tabular-nums" data-testid="brain-alignment">{Math.round(alignment * 100)}%</span></div>
          </div>

          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
            <Tile label="Streak"    value={stats?.streak || 0}/>
            <Tile label="XP"        value={stats?.xp || 0}/>
            <Tile label="Level"     value={stats?.level || 1}/>
            <Tile label="Sessions" value={(stats?.thought_defense?.plays || 0) + (stats?.reframe_battle?.plays || 0) + (stats?.breath_rhythm?.sessions || 0)}/>
          </div>

          <div className="mt-6 flex justify-center gap-3">
            <Link to="/games" className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border border-white/20 hover:bg-white/10 font-semibold">More games</Link>
            <button onClick={() => { if (navigator.share) navigator.share({title: 'My Brain Today', text: `${stage} · ${Math.round(alignment*100)}% light side. Training with Kickass Course.`, url: window.location.href}).catch(()=>{}); }} className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold" data-testid="brain-share"><Share2 size={14}/> Share my brain</button>
          </div>
        </div>
        {!user && <div className="mt-4 text-center text-sm text-white/60">Log in so your work is reflected in the brain.</div>}
      </div>
    </div>
  );
}

const Tile = ({ label, value }) => (
  <div className="bg-white/5 border border-white/10 rounded-[var(--kc-radius-sm)] p-3 text-center">
    <div className="font-display text-2xl text-[color:var(--kc-gold)] tabular-nums">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-white/60">{label}</div>
  </div>
);
